import React from 'react';
import { useState, useEffect } from 'react';
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import EventList from './EventList.js';
import { 
  parseISODate, 
  isSameDay, 
  createISOString, 
  monthNames, 
  formatDateDisplay 
} from '../utils/dateUtils.js';

export default function EventCalendarView({ events, onOpenLogin, onEventAdded }) {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [nextMonthDate, setNextMonthDate] = useState(new Date(new Date().setMonth(currentDate.getMonth() + 1)));
  const [selectedDate, setSelectedDate] = useState(null);
  const [calendarDays, setCalendarDays] = useState([]);
  const [nextMonthCalendarDays, setNextMonthCalendarDays] = useState([]);
  const [selectedDayEvents, setSelectedDayEvents] = useState([]);
  
  const now = new Date();
  const localToday = new Date(now.getTime() - now.getTimezoneOffset() * 60000).toISOString();
  

  const dayNames = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
  
  // Generate calendar days for a given month
  const generateCalendarDays = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth() + 1;
    
    const daysInMonth = new Date(year, month, 0).getDate();
    const firstDayOfMonth = new Date(year, month - 1, 1).getDay();
    const adjustedFirstDay = firstDayOfMonth === 0 ? 6 : firstDayOfMonth - 1;
    
    const days = [];
    
    // Add previous month's days
    const prevMonthDays = new Date(year, month - 1, 0).getDate();
    for (let i = adjustedFirstDay - 1; i >= 0; i--) {
      days.push({
        dateString: createISOString(year, month - 1, prevMonthDays - i),
        isCurrentMonth: false
      });
    }

    // Add current month's days
    for (let i = 1; i <= daysInMonth; i++) {
      days.push({
        dateString: createISOString(year, month, i),
        isCurrentMonth: true
      });
    }

    // Add next month's days
    const remainingDays = 42 - days.length;
    for (let i = 1; i <= remainingDays; i++) {
      days.push({
        dateString: createISOString(year, month + 1, i),
        isCurrentMonth: false
      });
    }

    return days;
  };

  // Generate calendar days for both months
  useEffect(() => {
    setCalendarDays(generateCalendarDays(currentDate));
    setNextMonthCalendarDays(generateCalendarDays(nextMonthDate));
  }, [currentDate, nextMonthDate]);

  // Update next month when current month changes
  useEffect(() => {
    const nextMonth = new Date(currentDate);
    nextMonth.setMonth(currentDate.getMonth() + 1);
    setNextMonthDate(nextMonth);
  }, [currentDate]);

  // Update event filtering logic
  useEffect(() => {
    if (selectedDate) {
      const dayEvents = events.filter(event => isSameDay(event.StartDateTime, selectedDate));
      setSelectedDayEvents(dayEvents);
    } else {
      // Show all events for current month only
      const monthEvents = events.filter(event => {
        const eventDate = parseISODate(event.StartDateTime);
        return eventDate.month === currentDate.getMonth() + 1 &&
               eventDate.year === currentDate.getFullYear();
      });
      setSelectedDayEvents(monthEvents);
    }
  }, [selectedDate, events, currentDate]);

  const hasEventsOnDay = (dateString, isCurrentMonth) => {
    if (!isCurrentMonth) return false;  // Don't show events for non-current month days
    return events.some(event => isSameDay(event.StartDateTime, dateString));
  };

  const hasEventsInMonth = (monthOffset) => {
    const targetDate = new Date(
      currentDate.getFullYear(),
      currentDate.getMonth() + monthOffset,
      1
    );
    
    return events.some(event => {
      const eventDate = parseISODate(event.StartDateTime);
      return eventDate.month === targetDate.getMonth() + 1 &&
             eventDate.year === targetDate.getFullYear();
    });
  };

  const navigateMonth = (direction) => {
    setCurrentDate(new Date(
      currentDate.getFullYear(),
      currentDate.getMonth() + direction,
      1
    ));
    setSelectedDate(null);
  };

  const hasPreviousMonthEvents = hasEventsInMonth(-1);
  const hasNextMonthEvents = hasEventsInMonth(1);

  const renderMonth = (date, days, isNextMonth = false) => (
    <div className={`${isNextMonth ? 'hidden md:block' : ''}`}>
      <div className="flex items-center justify-between mb-2">
        {!isNextMonth && hasPreviousMonthEvents ? (
          <button
            onClick={() => navigateMonth(-1)}
            className="p-2 text-white bg-slate-900 rounded-full hover:bg-white hover:text-slate-900"
          >
            <ChevronLeftIcon className="h-6 w-6" />
          </button>
        ) : (
          <div className="w-10 h-10" />
        )}
        
        <h2 className="text-3xl font-bold text-white">
          {monthNames[date.getMonth()]} {date.getFullYear()}
        </h2>
        
        {!isNextMonth && hasNextMonthEvents ? (
          <button
            onClick={() => navigateMonth(1)}
            className="p-2 text-white bg-slate-900 rounded-full hover:bg-white hover:text-slate-900"
          >
            <ChevronRightIcon className="h-6 w-6" />
          </button>
        ) : (
          <div className="w-10 h-10" />
        )}
      </div>

      <div className="bg-white shadow-lg">
        {/* Day headers */}
        <div className="grid grid-cols-7 gap-px">
          {dayNames.map(day => (
            <div key={day} className="px-2 py-2 text-center text-sm font-medium text-white bg-slate-900">
              {day}
            </div>
          ))}
        </div>

        {/* Calendar days */}
        <div className="grid grid-cols-7 gap-px">
          <div className="contents">
            {days.map((day, idx) => {
              
              const isToday = isSameDay(day.dateString, localToday);       
              const isSelected = selectedDate ? isSameDay(day.dateString, selectedDate) : false;
              const hasEvents = hasEventsOnDay(day.dateString, day.isCurrentMonth);
              const isStartOfWeek = idx % 7 === 0;
              const dayInfo = parseISODate(day.dateString);

              return (
                <React.Fragment key={idx}>
                  {isStartOfWeek && idx !== 0 && (
                    <div className="col-span-7 border-t border-gray-200 -mt-px"></div>
                  )}
                  <button
                    onClick={() => setSelectedDate(day.dateString)}
                    className={`
                      relative px-3 py-2 hover:bg-gray-50 focus:z-10 flex justify-center
                      ${!day.isCurrentMonth ? 'bg-gray-200 text-gray-400' : 'text-gray-700'}
                      ${isSelected ? 'bg-gray-100' : ''}
                    `}
                  >
                    <time
                      dateTime={day.dateString}
                      className={`
                        flex h-6 w-6 items-center justify-center rounded-full
                        ${isToday ? 'bg-blue-600 text-white' : ''}
                        ${hasEvents && !isToday ? 'bg-slate-900 text-white' : ''}
                      `}
                    >
                      {dayInfo.day}
                    </time>
                  </button>
                </React.Fragment>
              );
            })}
          </div>
        </div>
      </div>
    </div>
    
  );

  return (
    
    <div className="max-w-7xl mx-auto px-0 mt-[80px] md:mt-0">
      <div className="md:grid md:grid-cols-2 md:gap-8">
        {renderMonth(currentDate, calendarDays)}
        {renderMonth(nextMonthDate, nextMonthCalendarDays, true)}
      </div>

      {/* Events List */}
      <div className="mt-4">
        <div className="flex justify-between items-center mb-2">
          <h3 className="text-lg font-semibold text-white">
            Events for {selectedDate 
              ? formatDateDisplay(selectedDate)
              : `${monthNames[currentDate.getMonth()]} ${currentDate.getFullYear()}`}
          </h3>
          {selectedDate && (
            <button 
              onClick={() => setSelectedDate(null)}
              className="text-white font-normal underline"
            >
              Show All
            </button>
          )}
        </div>
        {selectedDayEvents.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {selectedDayEvents.map(event => (
              <EventList 
                key={event.EventID} 
                event={event}
                onOpenLogin={onOpenLogin}
                onEventAdded={onEventAdded}
              />
            ))}
          </div>
        ) : (
          <p className="text-white font-italic">
            No events scheduled for this {selectedDate ? 'day' : 'month'}.
          </p>
        )}
      </div>
    </div>
  );
} 